import React, { useEffect } from 'react';
import { useCookieConsent } from '@/context/CookieConsentContext';
import Logger from '@/utils/logger';

declare global {
  interface Window {
    fbq: any;
    _fbq: any;
  }
}

const FACEBOOK_PIXEL_ID = '715938994406157';

const FacebookPixel: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { preferences, consentGiven } = useCookieConsent();

  useEffect(() => {
    // Only initialize Facebook Pixel if consent is given and marketing cookies are accepted
    if (consentGiven && preferences.marketing) {
      initializeFacebookPixel();
    }
  }, [consentGiven, preferences.marketing]);

  const initializeFacebookPixel = () => {
    try {
      // If script already exists, do not re-inject
      if (document.querySelector(`script[src*="connect.facebook.net"]`)) {
        return;
      }

      // Initialize Facebook Pixel base code
      (function(f: any, b: any, e: any, v: any, n?: any, t?: any, s?: any) {
        if (f.fbq) return;
        n = f.fbq = function() {
          n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
        };
        if (!f._fbq) f._fbq = n;
        n.push = n;
        n.loaded = !0;
        n.version = '2.0';
        n.queue = [];
        t = b.createElement(e);
        t.async = !0;
        t.src = v;
        s = b.getElementsByTagName(e)[0];
        s.parentNode.insertBefore(t, s);
      })(
        window,
        document,
        'script',
        'https://connect.facebook.net/en_US/fbevents.js'
      );

      // Wait for script to load then initialize
      const checkFbq = () => {
        if (window.fbq && typeof window.fbq === 'function') {
          // Initialize the pixel with the ID
          window.fbq('init', FACEBOOK_PIXEL_ID);
          
          // Track the initial page view
          window.fbq('track', 'PageView');
          
          Logger.info('Facebook Pixel initialized successfully.');
        } else {
          // Retry after a short delay
          setTimeout(checkFbq, 100);
        }
      };

      checkFbq();

    } catch (error) {
      Logger.error('Failed to initialize Facebook Pixel:', error);
    }
  };

  return <>{children}</>;
};

// Utility functions for tracking Facebook Pixel events
export const trackFBEvent = (eventName: string, parameters?: any) => {
  if (typeof window !== 'undefined' && window.fbq) {
    window.fbq('track', eventName, parameters);
  }
};

export const trackFBPageView = () => {
  if (typeof window !== 'undefined' && window.fbq) {
    window.fbq('track', 'PageView');
  }
};

export default FacebookPixel;